import { Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Module } from "../../model/ModuleModel";
import { AbstractComponent } from '../abstract.component';
import { ButtonOptions } from '../eva-button/eva-button.component';
import { TagTextData } from '../tag-text/tag-text.component';
import { MediaPictureService } from '../../services/media-picture.service';
import { FormsService } from 'projects/common/services/forms.service';
import { InputModel } from "./inputs.model";
import { HttpClient } from "@angular/common/http";

declare var window: any;

export interface FormModuleConfiguration {
  form: Form,
}

export interface Form {
  title?: TagTextData,
  description?: TagTextData,
  text_position?: 'TOP' | 'CENTER' | 'BOTTOM' | 'LEFT' | 'RIGHT'
  image?: string,
  steps: Step[],
  button?: ButtonOptions,
  thanks_message?: ThanksMessage
}

export interface ThanksMessage {
  image: string,
  title: TagTextData,
  description: TagTextData,
  button: ButtonOptions
}

export interface SpecialTrackConfiguration {
  data: { [key: string]: any },
  url: string,
  encode_into_field?: string,
}

export interface Step {
  label: string,
  sections: Section[],
  prev_button: ButtonOptions,
  next_button: ButtonOptions,
  submit_button: ButtonOptions,
  track_form_data_on_submit: boolean,
  special_track_configuration?: SpecialTrackConfiguration,
}

export interface Section {
  title: string,
  description: string,
  inputs: InputModel[][],
}

@Component({
  selector: 'form-module',
  template: `
      <div class="form-module text-{{module.data.form.text_position}}"
           [ngClass]="{'-show-thanks-message': showThanksMessage}">
        <ng-container *ngIf="!showThanksMessage">
          <div class="form-title-section" *ngIf="module.data.form.title || module.data.form.description">
            <img class="form-image" *ngIf="mediaPictureService.getMediaUrl(module.data.form.image) as src" [src]="src" alt="">
            <tag-text class="form-title" *ngIf="module.data.form.title" [data]="module.data.form.title"></tag-text>
            <tag-text class="form-description" *ngIf="module.data.form.description"
                      [data]="module.data.form.description"></tag-text>
            <eva-button class="form-button" *ngIf="module.data.form.button && screenType !== 'MOBILE'"
                        [options]="module.data.form.button"></eva-button>
          </div>

          <div class="form-section">
            <div class="steps-container" *ngIf="module.data.form.steps.length > 1">
                        <span class="step" *ngFor="let step of module.data.form.steps; let i = index"
                              [ngClass]="{'-success': stepIndex > i, '-disabled': stepIndex < i}"
                              (click)="changeStep(i)">
                            <div class="step-number">
                                <i *ngIf="stepIndex > i" class="eva-3-icon-checkmark"></i>
                                <ng-container *ngIf="stepIndex <= i">{{ i + 1 }}</ng-container>
                            </div>
                            <p class="step-label -eva-3-bold" *ngIf="step.label">{{ step.label }}</p>
                        </span>
            </div>

            <div class="form-container" *ngIf="module.data.form.steps[stepIndex] as step">
              <div *ngFor="let section of step.sections" class="section-container">
                <div class="section-title-container" *ngIf="section.title || section.description">
                  <p class="section-title -eva-3-bold" *ngIf="section.title">{{ section.title }}</p>
                  <p class="section-description" *ngIf="section.description">{{ section.description }}</p>
                </div>
                <inputs-wrapper [model]="section.inputs" [formData]="formData[stepIndex]" [formGroupedValues]="formGroupedValues"
                                [showValidations]="showValidations" [upaData]="upaData" [groupError]="groupFilteredValuesError" (groupedInputsUpdateEvent)="onGroupedInputsUpdateEvent()"></inputs-wrapper>

              </div>

              <div class="step-buttons-container" *ngIf="step.next_button || step.prev_button || step.submit_button">
                <eva-button *ngIf="step.prev_button" class="step-next-button" [options]="step.prev_button"
                            (clickEvent)="prev()" [moduleId]="module.id" [moduleIndex]="module.row_number"
                            [uniqueId]="module.unique_id"></eva-button>
                <eva-button *ngIf="step.next_button" class="step-next-button" [options]="step.next_button"
                            (clickEvent)="next()" [moduleId]="module.id" [moduleIndex]="module.row_number"
                            [uniqueId]="module.unique_id"></eva-button>
                <eva-button *ngIf="step.submit_button" class="step-next-button" [options]="getSubmitButtonOptions(step)"
                            (clickEvent)="submit()" [showLoading]="submitLoading"
                            [moduleId]="module.id" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id"
                            [disableHref]="true" [upaData]="getSubmitButtonTrackingData(step)"></eva-button>
              </div>
              <p class="submit-error" *ngIf="showSubmitError">{{ 'forms.errors.submit' | translate }}</p>
            </div>
          </div>

          <eva-button class="form-button" *ngIf="module.data.form.button && screenType === 'MOBILE'"
                      [options]="module.data.form.button"></eva-button>
        </ng-container>

        <div *ngIf="showThanksMessage && thanksMessage && (thanksMessage.title || thanksMessage.description)"
             class="thanks-message-container">
          <img *ngIf="mediaPictureService.getMediaUrl(thanksMessage.image) as src" [src]="src" alt="thanks">
          <tag-text class="thanks-title" *ngIf="thanksMessage.title" [data]="thanksMessage.title"></tag-text>
          <tag-text class="thanks-description" *ngIf="thanksMessage.description"
                    [data]="thanksMessage.description"></tag-text>
          <eva-button *ngIf="thanksMessage.button" class="thanks-button" [options]="thanksMessage.button"
                      [moduleId]="module.id" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id"></eva-button>
        </div>
      </div>
    `,
  styleUrls: ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class FormModuleComponent extends AbstractComponent implements OnInit {
  @Input() override module: Module<FormModuleConfiguration>

  formData: any[] = [{}];
  formGroupedValues: any = {};
  stepIndex: number = 0;
  showValidations: boolean;
  currentStep: Step;
  showThanksMessage: boolean;
  thanksMessage: ThanksMessage | null;
  submitLoading: boolean;
  showSubmitError: boolean;
  upaData: any;
  groupFilteredValuesError: boolean;

  constructor(injector: Injector,
    public mediaPictureService: MediaPictureService,
    private formsService: FormsService,
    private httpClient: HttpClient) {
    super(injector);
  }

  ngOnInit() {
    this.updateCurrentStep();
    this.thanksMessage = this.module.data.form.thanks_message || null;
    this.upaData = { label: '', uniqueId: this.module.unique_id, moduleId: this.module.id, moduleIndex: this.module.row_number };
  }

  getModuleId(): string {
    return "FormModule";
  }

  submit() {
    if (!this.showValidations) {
      this.validateGroupedInputs();
    }
    this.showValidations = true;
    if (this.validateAllInputs()) {
      if (this.currentStep.submit_button.form_type) {
        const formJson = {};
        this.submitLoading = true;
        this.showSubmitError = false;

        this.formData.forEach(stepData => {
          Object.entries(stepData).forEach(([key, value]) => {
            if (!formJson[key]) {
              formJson[key] = value;
            } else {
              formJson[key] += '|' + value;
            }
          });
        });

        // Si es trip planner, no se guarda el formulario, solo se redige
        // NOTE: Si esto se va a extender para otros casos, refactorizar para que se envie por parametro
        if (this.currentStep.submit_button.form_type === 'TRIP_PLANNER' && this.currentStep.submit_button.link) {
          this.submitLoading = false;
          this.goToUrl(this.currentStep.submit_button.link);
          return;
        }

        if (this.currentStep.special_track_configuration) {
          this.doSpecialTrack(this.currentStep.special_track_configuration, formJson);
        }

        this.formsService.submit(formJson, this.currentStep.submit_button.form_type).then((response) => {
          setTimeout(() => {
            this.submitLoading = false;

            if (this.currentStep.submit_button.link) {
              this.goToUrl(this.currentStep.submit_button.link);
            } else {
              this.showThanksMessage = true;
              window.scrollTo({ top: 0, behavior: "smooth" });
            }

          }, (this.currentStep.submit_button.seconds_to_sleep || 0) * 1000);

        }, (error) => {
          this.submitLoading = false;
          this.showSubmitError = true;
        });
      } else if (this.currentStep.submit_button.link) {

        this.goToUrl(this.currentStep.submit_button.link);
      }

    }
  }

  doSpecialTrack = (trackConfig: SpecialTrackConfiguration, formJson: any) => {
    let data = { ...trackConfig.data, ...formJson };
    if (trackConfig.encode_into_field) {
      data = { [trackConfig.encode_into_field]: btoa(JSON.stringify(data)) }
    }
    return new Promise((resolve, reject) => {
      this.httpClient.post(trackConfig.url, data)
        .subscribe(response => resolve(response), error => reject(error));
    });
  }

  goToUrl(url) {
    if (url.includes("${")) {
      url = this.setLinkQueryParams(url);
    }
    window.location = url;
  }

  next() {
    if (this.validateCurrentStep()) {
      this.showValidations = false;
      if (this.stepIndex < this.module.data.form.steps.length - 1) {
        this.stepIndex++;
        if (!this.formData[this.stepIndex]) {
          this.formData[this.stepIndex] = {};
        }
      }
      if (this.currentStep.next_button.link) {
        window.location = this.currentStep.next_button.link;
      }
      this.updateCurrentStep();
    } else {
      this.showValidations = true;
    }
  }

  prev() {
    if (this.stepIndex > 0) {
      this.stepIndex--;
      this.formData[this.stepIndex];
    }
    if (this.currentStep.prev_button.link) {
      window.location = this.currentStep.prev_button.link;
    }
    if (this.stepIndex === 0 && !this.currentStep.prev_button.link) {
      globalThis.history.back();
    }

    this.updateCurrentStep();
  }

  changeStep = (newStep: number) => {
    if (newStep < this.stepIndex) {
      this.stepIndex = newStep
      this.updateCurrentStep();
    }
  }

  validateAllInputs(): boolean {
    // TODO probar alternativas
    const invalidInputs = this.module.data.form.steps.find(step => step.sections.find(section => this.validateInputs(section.inputs)));
    return invalidInputs == null;
  }

  validateCurrentStep(): boolean {
    const invalidInputs = this.module.data.form.steps[this.stepIndex].sections.find(section => this.validateInputs(section.inputs));
    return invalidInputs == null;
  }

  validateInputs(inputs: InputModel[][]) {
    return inputs.find(inputRow => inputRow.find(input => {
      let isInvalid = input.valid != undefined && !input.valid;
      if (input.options) {
        const anyOptionsInvalid = input.options.find(option => option.inputs && this.validateInputs(option.inputs)) != null;
        isInvalid = isInvalid || anyOptionsInvalid;
      }
      return isInvalid;
    }));
  }

  onGroupedInputsUpdateEvent() {
    setTimeout(() => { this.validateGroupedInputs() }, 0);
  }

  validateGroupedInputs() {
    let groupErrorFound = false;

    this.module.data.form.steps
      .forEach(step => step.sections
        .forEach(section => section.inputs
          .forEach(inputsRow => inputsRow.forEach(input => {
            const groupId = input.validations?.group_filtered_values?.id;
            if (groupId && this.formGroupedValues[groupId]) {
              input.valid = input.valid && this.formGroupedValues[groupId].isValid;
              groupErrorFound = groupErrorFound || !this.formGroupedValues[groupId].isValid;
            }
          })
          )));
    this.groupFilteredValuesError = groupErrorFound;
    if (groupErrorFound) {
      this.showValidations = true;
    }
  }

  updateCurrentStep() {
    this.currentStep = this.module.data.form.steps[this.stepIndex];
  }

  getSubmitButtonOptions(step) {
    return { ...step.submit_button, type: this.validateAllInputs() ? step.submit_button.type : '-disable' };
  }

  setLinkQueryParams(link) {
    this.submitLoading = true;
    this.showSubmitError = false;
    const formData = this.formData[0];

    return link.replace(/\${(.*?)}/g, (match, variable) => {
      return encodeURIComponent(formData[variable]) || match;
    });
  }

  getSubmitButtonTrackingData = (step: Step): { data: { [key: string]: string } } | undefined => {
    if (step.track_form_data_on_submit) {
      let data: { [key: string]: string } = {};
      Object.entries(this.formData[this.stepIndex]).forEach(([key, value]) => data[key] = String(value));
      return { data: data };
    }
    return undefined;
  }
}
